const PLAYER_SELECTOR = '[data-video-player]';
const PLACEHOLDER_SELECTOR = '[data-video-placeholder]';
const TRIGGER_SELECTOR = '[data-video-trigger]';
const TARGET_SELECTOR = '[data-video-target]';
const TEMPLATE_SELECTOR = 'template[data-video-template]';
const INITIALIZED_ATTRIBUTE = 'data-video-initialized';

const AUTOPLAY_PARAM = 'autoplay';

const KEYBOARD_ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

function appendAutoplayParam(url) {
    if (!url) {
        return url;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);

        if (!parsedUrl.searchParams.has(AUTOPLAY_PARAM)) {
            parsedUrl.searchParams.set(AUTOPLAY_PARAM, '1');
        }

        // YouTube-specific parameters keep UX tighter inside embeds.
        if (parsedUrl.hostname.includes('youtube')) {
            parsedUrl.searchParams.set('rel', '0');
            parsedUrl.searchParams.set('playsinline', '1');
        }

        return parsedUrl.toString();
    } catch (error) {
        if (url.includes(`${AUTOPLAY_PARAM}=`)) {
            return url;
        }

        const joinChar = url.includes('?') ? '&' : '?';
        return `${url}${joinChar}${AUTOPLAY_PARAM}=1`;
    }
}

function fadeOutPlaceholder(placeholder) {
    if (!placeholder) {
        return;
    }

    requestAnimationFrame(() => {
        placeholder.style.pointerEvents = 'none';
        placeholder.style.opacity = '0';
        const fallbackTimeout = window.setTimeout
            ? window.setTimeout(() => {
                  placeholder.remove();
              }, 500)
            : null;
        placeholder.addEventListener(
            'transitionend',
            () => {
                if (fallbackTimeout) {
                    window.clearTimeout(fallbackTimeout);
                }
                placeholder.remove();
            },
            { once: true }
        );
    });
}

function mountEmbedContent(fragment, container) {
    const iframe = fragment.querySelector('iframe');

    if (!iframe) {
        return;
    }

    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', '100%');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.bottom = '0';
    iframe.style.right = '0';

    const externalUrl = container.dataset.videoExternalUrl;

    if (!iframe.getAttribute('src') && externalUrl) {
        iframe.src = appendAutoplayParam(externalUrl);
    } else {
        const currentSrc = iframe.getAttribute('src');
        if (currentSrc) {
            iframe.src = appendAutoplayParam(currentSrc);
        }
    }

    const allow = iframe.getAttribute('allow') || '';
    if (!allow.includes('autoplay')) {
        iframe.setAttribute('allow', `${allow};autoplay`.replace(/^;+/, ''));
    }
}

function mountVideoContent(fragment) {
    const videoElement = fragment.querySelector('video');

    if (!videoElement) {
        return;
    }

    videoElement.style.width = '100%';
    videoElement.style.height = '100%';

    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('playsinline', '');

    const playPromise = videoElement.play();

    if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
            // Ignore autoplay rejection; controls remain accessible regardless.
        });
    }
}

function activatePlayer(container) {
    if (
        !container ||
        container.getAttribute(INITIALIZED_ATTRIBUTE) === 'true'
    ) {
        return;
    }

    const template = container.querySelector(TEMPLATE_SELECTOR);
    const target = container.querySelector(TARGET_SELECTOR);

    if (!template || !target) {
        return;
    }

    const fragment = template.content ? template.content.cloneNode(true) : null;

    if (!fragment) {
        return;
    }

    const mode = container.dataset.videoMode;

    if (mode === 'embed') {
        mountEmbedContent(fragment, container);
    } else if (mode === 'file') {
        mountVideoContent(fragment);
    }

    target.innerHTML = '';
    target.appendChild(fragment);

    const placeholder = container.querySelector(PLACEHOLDER_SELECTOR);
    fadeOutPlaceholder(placeholder);

    const trigger = container.querySelector(TRIGGER_SELECTOR);
    if (trigger) {
        trigger.remove();
    }

    container.classList.add('is-playing');
    container.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
}

function registerPlayer(container) {
    if (!container || container.hasAttribute(INITIALIZED_ATTRIBUTE)) {
        return;
    }

    const trigger = container.querySelector(TRIGGER_SELECTOR);

    if (!trigger) {
        return;
    }

    const handleClick = () => {
        activatePlayer(container);
        cleanup();
    };

    const handleKeydown = event => {
        if (!KEYBOARD_ACTIVATION_KEYS.includes(event.key)) {
            return;
        }

        event.preventDefault();
        activatePlayer(container);
        cleanup();
    };

    function cleanup() {
        trigger.removeEventListener('keydown', handleKeydown);
        trigger.removeEventListener('click', handleClick);
    }

    trigger.addEventListener('click', handleClick, { once: true });
    trigger.addEventListener('keydown', handleKeydown);

    container.setAttribute(INITIALIZED_ATTRIBUTE, 'pending');
}

function initializeVideoBlocks(scope = document) {
    const contextElement = scope instanceof Element ? scope : null;
    const root = contextElement || document;

    if (contextElement && contextElement.matches(PLAYER_SELECTOR)) {
        registerPlayer(contextElement);
    }

    const players = root.querySelectorAll(
        `${PLAYER_SELECTOR}:not([${INITIALIZED_ATTRIBUTE}])`
    );

    players.forEach(player => {
        registerPlayer(player);
    });
}

function onReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
        callback();
    }
}

onReady(() => {
    initializeVideoBlocks(document);
});

if (
    typeof window !== 'undefined' &&
    window.acf &&
    typeof window.acf.addAction === 'function'
) {
    window.acf.addAction(
        'render_block_preview/type=video',
        function (block, element) {
            let scope = null;

            if (element) {
                scope =
                    element instanceof Element ? element : element[0] || null;
            }

            if (!scope && block) {
                if (block instanceof Element) {
                    scope = block;
                } else if (block[0]) {
                    scope = block[0];
                } else if (block.el) {
                    scope =
                        block.el instanceof Element
                            ? block.el
                            : block.el[0] || null;
                }
            }

            initializeVideoBlocks(scope || document);
        }
    );
}
